import MainLayout from 'layouts/MainLayout';
import { InteractiveDemo, Home, NotFound } from 'pages';
import { Routes, Route } from 'react-router-dom';
import { lazy, Suspense } from 'react';
import AuthLayout from 'layouts/AuthLayout';
import {
	ForgotPassword,
	Signup,
	ResetPassword,
	TwoFactorAuthentication,
	VerifyOTP,
	AccountVerify,
} from 'components/Auth';
import SeekerProfile from 'pages/SeekerProfile';
import CompanyProfile from 'pages/CompanyProfile';
import JobPreference from 'pages/JobPreference';
import PublicRoute from './PublicRoute';
import ProtectedRoute from './ProtectedRoute';
import Unauthorized from 'pages/Unauthorized';
import JobDetail from 'pages/JobDetail';
import PostNewJob from 'pages/PostNewJob';
import MyPostedJobs from 'pages/MyPostedJobs';
import ScheduledMeetings from 'pages/ScheduledMeetings';
import AppliedJobs from 'pages/AppliedJobs';
import SidebarLayout from 'layouts/SidebarLayout';
import MyConnections from 'pages/MyConnections';
import FollowingsList from 'pages/FollowingsList';
import HiringBoard from 'pages/HiringBoard';
import Notifications from 'pages/Notifications';
import ChatBox from 'pages/ChatBox';
import CurrentPlanHistory from 'pages/CurrentPlanHistory';
import PrivacyPolicy from 'pages/StaticPages/PrivacyPolicy';
import TermsCondition from 'pages/StaticPages/TermsCondition';
import ContactUs from 'pages/StaticPages/ContactUs';
import FAQs from 'pages/StaticPages/FAQs';
import Blogs from 'pages/StaticPages/Blogs';
import BlogDetail from 'pages/StaticPages/BlogDetail';
import SocialFeedPosts from 'pages/SocialFeed/SocialFeedPosts';
import MembersResult from 'pages/SocialFeed/MembersResult';
import PostDetail from 'pages/SocialFeed/PostDetail';
import MyMatches from 'pages/MyMatches';
import PopularJobs from 'pages/PopularJobs';
import FavouriteJobs from 'pages/FavouriteJobs';
import FilteredJobs from 'pages/FilteredJobs';
import ChangePassword from 'pages/ChangePassword';
import JobAlertList from 'pages/JobAlertList';
import useAuthStore from 'store/auth';
import CompanyBoxLayout from 'layouts/CompanyBoxLayout';
import ApplyJobAssessment from 'pages/ApplyJobAssessment';
import CreateQuestionnaire from 'pages/CreateQuestionnaire';
import ApplicantsList from 'pages/ApplicantsList';
import SavedTalentList from 'pages/SavedTalentList';
import InactiveJobs from 'pages/InactiveJobsList';
import EditQuestionnaire from 'pages/EditQuestionnaire';
import SocialLayout from 'layouts/SocialLayout';
import HowItWorks from 'components/Home/HowItWorks';
import { USER_TYPES } from 'constants/index';
import ResumeTemplates from 'pages/ResumeTemplates';
import ResumeBuilder from 'pages/ResumeBuilder';
import HashTagProfile from 'pages/HashtagProfile';
import MyFeeds from 'pages/SocialFeed/MyFeeds';
import ScheduledMeetingsCompany from 'pages/ScheduledMeetingsCompany';
import FollowerList from 'pages/FollowerList';
import ChatLayout from 'layouts/ChatLayout';
import ChatListMobile from 'pages/ChatBox/ChatListMobile';
import RedirectPage from 'pages/RedirectPage';
import Advertisement from 'pages/Advertisement';
import LoginPage from 'components/Auth/Login';
import Subscriptions from 'pages/Subscriptions';
import MobileResumePreview from 'components/ResumeBuilder/ResumeForm/MobileResumePreview';
import ApplicantAssessment from 'pages/ApplicantAssessment';
import RecentActivity from 'components/Common/RecentActivity';
import Zendesk from 'pages/Zendesk/Zendesk';
import { PaymentFailed, PaymentSuccess } from 'pages/Payment';
import ProfileSetup from 'pages/ProfileSetup/ProfileSetup';
import About from 'pages/StaticPages/About';
import Imprint from 'pages/StaticPages/Imprint';
import Cancellationpolicy from 'pages/StaticPages/Cancellationpolicy';
import StaticPageMobile from 'pages/StaticPages/StaticPageMobile';
import Faqs from 'pages/StaticPages/StaticPageMobile/Faqs';
import HowItWorksPage from 'pages/StaticPages/HowItWorksPage';
import CookiesPolicy from 'pages/StaticPages/CookiesPolicy';
import CaliforniaPrivacyPolicy from 'pages/StaticPages/CaliforniaPrivacyPolicy';
import EuropeanPrivacyPolicy from 'pages/StaticPages/EuropeanPrivacyPolicy';

// Lazy load heavy components
const LazyResumeBuilder = lazy(() => import('pages/ResumeBuilder'));
const LazyChatBox = lazy(() => import('pages/ChatBox'));
const LazyResumeTemplates = lazy(() => import('pages/ResumeTemplates'));

// Loading component for Suspense fallback
const LoadingFallback = () => (
	<div className="d-flex justify-content-center align-items-center" style={{ minHeight: '200px' }}>
		<div className="spinner-border text-primary" role="status">
			<span className="visually-hidden">Loading...</span>
		</div>
	</div>
);

const Routing = () => {
	const { user: userDetails = {}, token: isAuthenticated }: any = useAuthStore(
		(state) => state?.userInfo || {},
	);
	return (
		<Routes>
			{/* Public Routes */}
			<Route element={<PublicRoute restricted={false} />}>
				<Route element={<AuthLayout />}>
					<Route path="profile-update" element={<ProfileSetup />} />
					<Route path="signup" element={<Signup />} />
					<Route path="login" element={<LoginPage />} />
					<Route path="forgot-password" element={<ForgotPassword />} />
					<Route path="verify-otp" element={<VerifyOTP />} />
					<Route path="reset-password" element={<ResetPassword />} />
					<Route path="two-factor-authentication" element={<TwoFactorAuthentication />} />
					<Route path="verify-account" element={<AccountVerify />} />
					<Route element={<SocialLayout />}>
						<Route path="/interactive-demo" element={<InteractiveDemo />} />
						<Route path="/static/profile" element={<SeekerProfile />} />
					</Route>
				</Route>
				<Route element={<MainLayout />}>
					<Route path="/" element={<Home />} />
					{(userDetails?.userType === USER_TYPES.SEEKER || !isAuthenticated) && (
						<Route path="job-search" element={<FilteredJobs />} />
					)}

					<Route element={userDetails?.userId ? <SidebarLayout /> : <CompanyBoxLayout />}>
						<Route path="job-detail/:id" element={<JobDetail />} />
					</Route>

					{/* Static Pages - Accessible to all users */}
					<Route path="privacy-policy" element={<PrivacyPolicy />} />
					<Route path="about" element={<About />} />
					<Route path="terms-conditions" element={<TermsCondition />} />
					<Route path="contact-us" element={<ContactUs />} />
					<Route path="faqs" element={<FAQs />} />
					<Route path="blogs" element={<Blogs />} />
					<Route path="blogs/:id" element={<BlogDetail />} />
					<Route path="how-it-works" element={<HowItWorksPage />} />
					<Route path="cookies-policy" element={<CookiesPolicy />} />
					<Route path="imprint" element={<Imprint />} />
					<Route path="cancellation-policy" element={<Cancellationpolicy />} />
					<Route path="california-privacy-policy" element={<CaliforniaPrivacyPolicy />} />
					<Route path="european-privacy-policy" element={<EuropeanPrivacyPolicy />} />
					<Route path="payment-check" element={<PaymentSuccess />} />
					<Route path="payment-failed" element={<PaymentFailed />} />
				</Route>
			</Route>

			{/* Protected Routes */}
			<Route path="/" element={<MainLayout />}>
				{/* Seeker Protected Routes */}
				<Route path="seeker" element={<ProtectedRoute allowedRoles={['seeker']} />}>
					<Route path="apply-job-assessment/:id" element={<ApplyJobAssessment />} />
					<Route
						path="resume-templates"
						element={
							<Suspense fallback={<LoadingFallback />}>
								<LazyResumeTemplates />
							</Suspense>
						}
					/>

					<Route element={<SidebarLayout />}>
						<Route index element={<SeekerProfile />} />
						<Route path="job-preference" element={<JobPreference />} />
						<Route path="profile" element={<SeekerProfile />} />
						<Route path="my-matches" element={<MyMatches />} />
						<Route path="popular-jobs" element={<PopularJobs />} />
						<Route path="favourite-jobs" element={<FavouriteJobs />} />
						<Route path="applied-jobs" element={<AppliedJobs />} />
						<Route path="suggested-jobs" element={<MyMatches />} />
						<Route path="saved-jobs-alert" element={<JobAlertList />} />
						<Route path="scheduled-meetings" element={<ScheduledMeetings />} />
					</Route>
					<Route path="preference" element={<JobPreference />} />
				</Route>
				{/* Company Protected Routes */}
				<Route path="company" element={<ProtectedRoute allowedRoles={['company']} />}>
					<Route path="meeting-link" element={<RedirectPage />} />
					<Route index element={<CompanyProfile />} />
					<Route path="subscriptions" element={<Subscriptions />} />
					<Route element={<SidebarLayout />}>
						<Route index element={<CompanyProfile />} />
						<Route path="profile" element={<CompanyProfile />} />
						<Route path="saved-talent" element={<SavedTalentList />} />
						<Route path="inactive-jobs" element={<InactiveJobs />} />
						<Route path="scheduled-meetings" element={<ScheduledMeetingsCompany />} />
						<Route path="advertisement" element={<Advertisement />} />
					</Route>
					<Route path="post-new-job" element={<PostNewJob />} />
					<Route path="edit-job/:id" element={<PostNewJob />} />
					<Route path="applicants/:id" element={<ApplicantsList />} />
					<Route path="my-jobs" element={<MyPostedJobs />} />
					<Route path="applicant-assessment/:jobId/:userId" element={<ApplicantAssessment />} />
					<Route path="create-questionnaire/:id?" element={<CreateQuestionnaire />} />
					<Route path="edit-questionnaire/:id" element={<EditQuestionnaire />} />
					<Route path="hiring-board/:id" element={<HiringBoard />} />
				</Route>
				{/* Common Protected Routes */}
				<Route path="/" element={<ProtectedRoute allowedRoles={['seeker', 'company']} />}>
					<Route path="company/jobs/:id" element={<MyPostedJobs />} />

					<Route element={<SidebarLayout />}>
						<Route path="seeker/followings-list" element={<FollowingsList />} />
						<Route path="seeker/my-connections" element={<MyConnections />} />
						<Route path="company/followings-list" element={<FollowingsList />} />
						<Route path="company/followers-list" element={<FollowerList />} />

						<Route path="seeker/profile/:id" element={<SeekerProfile />} />
						<Route path="company/profile/:id" element={<CompanyProfile />} />
						<Route path="change-password" element={<ChangePassword />} />
						<Route path="notifications" element={<Notifications />} />
						<Route path="current-plan-history" element={<CurrentPlanHistory />} />
					</Route>
				</Route>
			</Route>

			{/* Additional Protected Routes */}
			<Route path="/" element={<ProtectedRoute allowedRoles={['seeker', 'company']} />}>
				<Route
					path="seeker/resume-templates/:slug"
					element={
						<Suspense fallback={<LoadingFallback />}>
							<LazyResumeBuilder />
						</Suspense>
					}
				/>
				<Route path="/" element={<ChatLayout />}>
					<Route
						path="chat/:receiverId/:roomId"
						element={
							<Suspense fallback={<LoadingFallback />}>
								<LazyChatBox />
							</Suspense>
						}
					/>
					<Route path="chat-list" element={<ChatListMobile />} />
				</Route>
				<Route element={<SocialLayout />}>
					<Route path="hashtag/Profile/:id" element={<HashTagProfile />} />
					<Route path="seeker/members-result" element={<MembersResult />} />
					<Route path="company/members-result" element={<MembersResult />} />
					<Route path="social-feed-posts" element={<SocialFeedPosts />} />
					<Route path="my-feeds" element={<MyFeeds />} />
					<Route path="feed-detail/:id" element={<PostDetail />} />
					<Route path="all-activities/:id/:type" element={<RecentActivity />} />
				</Route>
			</Route>

			{/* Mobile and Special Routes */}
			<Route path="resume-mobile/:slug/:token" element={<MobileResumePreview />} />
			<Route path="static-page-mobile/faqs" element={<Faqs />} />
			<Route path="static-page-mobile/:slug" element={<StaticPageMobile />} />

			{/* Unauthorized Route */}
			<Route path="/unauthorized" element={<Unauthorized />} />

			{/* Catch-All Route for 404 Not Found */}
			<Route path="*" element={<NotFound />} />
		</Routes>
	);
};

export default Routing;
